import { BlueExternalLink } from '@hn-ui/links';
import { styled } from 'styled-components';
import { TripleArrow, WarningIcon } from './Common';

const Container = styled.section`
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  text-size-adjust: none;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 15px;
`;

const Header = styled.h2`
  font-size: 24px;
  font-weight: bold;
  line-height: 32px;
  color: ${({ theme }) => theme.colors.blackText || theme.colors.black10};
  margin: 0 0 8px 0;
`;

const Example = styled.p`
  font-size: 24px;
  line-height: 32px;
  margin: 0;
  white-space: nowrap;
`;

const DividingLine = styled.div`
  width: calc(100% + 48px);
  border-bottom: 2px #e3f2fc solid;
  margin-bottom: 24px;
`;

const ExampleInfo = styled.p`
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  margin: 0;
`;

const BodyContainer = styled.main`
  display: flex;
  flex-flow: column;
  align-items: center;
`;

const Table = styled.div`
  display: flex;
  flex-flow: column;
  margin-bottom: 22px;
`;
const TableRow = styled.div`
  display: flex;
  justify-content: center;
`;

const Cell = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;

  &:not(:last-child) {
    margin-right: 38px;
  }
`;

const Header2 = styled.h2`
  display: flex;
  align-items: center;
  text-align: center;
  font-weight: bold;
  font-size: 16px;
  line-height: 24px;
  margin: 0 0 8px;
`;

const SubHeader2 = styled.p`
  width: 100%;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  text-align: left;
  margin-bottom: 8px;
`;

const ScTripleArrow = styled(TripleArrow)`
  margin: 5px 32px 5px 0;
`;

const ScWarningIcon = styled(WarningIcon)`
  vertical-align: sub;
  margin-right: 8px;
`;

const ActivityStreamAccuracyHelp = ({ ...rest }) => (
  <Container {...rest} data-testid='activity-stream-accuracy-help'>
    <HeaderContainer>
      <Header>Interpreting task accuracy</Header>
    </HeaderContainer>
    <BodyContainer>
      <Example>7/10 (70%)</Example>
      <ScTripleArrow />
      <Table>
        <TableRow>
          <Cell>
            <ExampleInfo>
              Number of correct
              <br />
              attempts
            </ExampleInfo>
          </Cell>
          <Cell>
            <ExampleInfo>
              Total number
              <br />
              of attempts
            </ExampleInfo>
          </Cell>
          <Cell>
            <ExampleInfo>
              Percentage of correct
              <br />
              attempts
            </ExampleInfo>
          </Cell>
        </TableRow>
      </Table>
      <DividingLine />
      <Header2>
        <ScWarningIcon />
        What does the alert symbol mean?
      </Header2>
      <SubHeader2>
        The alert symbol appears next to tasks that appear to be causing
        problems for the student. We recommend re-solving alert taks with the
        student to find out where the points of difficulty are.
      </SubHeader2>
      <Header2>How are struggle tasks determined?</Header2>
      <SubHeader2>
        Sometimes, a low accuracy rate doesn&apos;t mean that a student has gaps
        in understanding. If he solves 1 out of 2 problems correctly (an
        accuracy rate of 50%), a simple typo could be the reason. However, if he
        solves 4 out of 8 problems correctly, the same accuracy rate of 50% is
        more problematic. To read more about how our methodologists have
        developed a unique formula to highlight only tasks where the number of
        incorrect responses is significant, open{' '}
        <BlueExternalLink href='https://help.happynumbers.com/en/articles/5907894-student-intervention-alert'>
          this article
        </BlueExternalLink>
        .
      </SubHeader2>
    </BodyContainer>
  </Container>
);

export { ActivityStreamAccuracyHelp };
